import type { ComparePageCopy, ComparePageCopyByLang } from "./compare";

const VENDOR = { name: "tl;dv", url: "https://tldv.io" };

const en: ComparePageCopy = {
  seo: {
    title: "tl;dv vs. Minuta — clips after vs. answers during",
    description:
      "tl;dv is built for recordings, clips, and shareable recaps after the call. Minuta is built for live English, Portuguese, and Spanish conversations where you need to know what to say next.",
    keywords:
      "tldv alternative, tldv vs minuta, no bot meeting copilot, real-time meeting copilot, tldv portuguese, ai meeting notes mac",
  },
  vendor: VENDOR,
  eyebrow: "Comparison",
  h1: "tl;dv vs. Minuta: clips after vs. answers during",
  intro:
    "tl;dv is the better fit when the meeting needs to become a recording, a highlight clip, or a recap teammates can share after. Minuta is for the live moment: the prospect asks a question in English, Portuguese, or Spanish, the room is waiting, and you need to know what to say before the conversation moves on.",
  honesty: {
    heading: "Pick the right one",
    pickThem: {
      name: "tl;dv",
      bullets: [
        "You want recordings, polished video clips, and reels of meeting moments — that's tl;dv's edge.",
        "You need shareable recaps for teammates who were not in the room.",
        "Your team's stack is Salesforce / HubSpot / Notion-heavy and the deep CRM integrations matter.",
        "Your priority is the after-call artifact: transcript, summary, clip, or CRM handoff.",
        "You don't mind a bot joining the call and notes living in tl;dv's cloud.",
      ],
    },
    pickMinuta: [
      "You need to know what to say during the conversation, not just after it ends.",
      "You need answer drafts shaped by your context and docs while the call is still happening.",
      "A bot in the call is a non-starter (regulated industry, paranoid prospect, sensitive deal).",
      "No bot joins your call — the other side never sees a notetaker. Minuta runs silently on your desktop.",
      "You need EN ↔ PT/ES live translation during the call (tl;dv transcribes many languages but does not translate live).",
    ],
  },
  table: {
    heading: "Side by side",
    headers: { feature: "Feature", them: "tl;dv", minuta: "Minuta" },
    rows: [
      {
        feature: "Bot in the meeting",
        them: {
          value: "Yes — joins as a meeting bot",
          cite: { url: "https://tldv.io", capturedAt: "2026-05-01" },
        },
        minuta: { value: "No — invisible, runs on your computer" },
      },
      {
        feature: "Real-time answer copilot",
        them: { value: "No — focused on recordings, clips, and recaps after" },
        minuta: { value: "Yes (Pro) — drafts answers as questions land" },
      },
      {
        feature: "Live translation (EN ↔ PT/ES)",
        them: { value: "No — multi-language transcription, but not live translation" },
        minuta: { value: "Yes — real-time" },
      },
      {
        feature: "Audio processing",
        them: {
          value: "Cloud",
          cite: { url: "https://tldv.io", capturedAt: "2026-05-01" },
        },
        minuta: { value: "Local capture, secure transient API" },
      },
      {
        feature: "Where notes live",
        them: { value: "tl;dv cloud" },
        minuta: { value: "On your computer (transient processing)" },
      },
      {
        feature: "Native PT website",
        them: { value: "No — English-only site" },
        minuta: { value: "Yes" },
      },
      {
        feature: "Native ES website",
        them: { value: "No — English-only site" },
        minuta: { value: "Yes" },
      },
      {
        feature: "Video clips & reels",
        them: {
          value: "Yes — their edge",
          cite: { url: "https://tldv.io", capturedAt: "2026-05-01" },
        },
        minuta: { value: "No — focused on live copilot, not clip editing" },
      },
      {
        feature: "Deep CRM integrations",
        them: {
          value: "Salesforce / HubSpot / Notion / Slack native",
          cite: { url: "https://tldv.io", capturedAt: "2026-05-01" },
        },
        minuta: { value: "Manual export (copy/paste, markdown)" },
      },
      {
        feature: "Free tier",
        them: {
          value: "~10 transcripts/month",
          cite: { url: "https://tldv.io/pricing", capturedAt: "2026-05-01" },
        },
        minuta: { value: "7-day full trial · then 1h/month Free" },
      },
      {
        feature: "Paid",
        them: {
          value: "Pro ~$29/mo (annual)",
          cite: { url: "https://tldv.io/pricing", capturedAt: "2026-05-01" },
        },
        minuta: { value: "€9/mo" },
      },
    ],
    footnote: "Pricing as of 2026-05-01; check each vendor for the current numbers.",
  },
  whenMinuta: {
    heading: "Where Minuta wins",
    bullets: [
      "Answers during the call. tl;dv is stronger when you need recordings, clips, and recaps after. Minuta drafts what to say while the question is still in the air.",
      "Context-aware drafting. Minuta can use your context and docs to shape the answer, instead of leaving you with a recap after the moment has passed.",
      "No bot in the call. Some prospects refuse to talk with a recording bot in the room. Minuta runs invisibly on your machine — no one but you knows it's there.",
      "No bot in the call. tl;dv joins as a meeting participant. Minuta listens from your desktop — the other side never sees a notetaker join. Transcripts, summaries, and copilot answers are stored in your desktop app.",
      "Live translation, not just multi-language transcription. tl;dv transcribes ~30 languages. Minuta translates between EN and PT/ES live, so you can speak your language and read theirs in real time.",
    ],
  },
  also: { heading: "Comparing other tools?", links: [] },
  cta: {
    heading: "Do more than recap the call.",
    body: "7 days with everything unlocked. Bring your context, get answer drafts during the call, and keep the bot out.",
    primary: "Start free trial",
    secondary: "Download for macOS",
  },
};

const pt: ComparePageCopy = {
  seo: {
    title: "tl;dv vs. Minuta — clipes depois vs. respostas durante",
    description:
      "tl;dv foi feito para gravações, clipes e recaps compartilháveis depois da call. Minuta foi feito para conversas em inglês, português e espanhol em que você precisa saber o que dizer agora.",
    keywords:
      "alternativa tldv, tldv vs minuta, copiloto de reunião em tempo real, transcrição de reunião no mac, tldv português, ata de reunião com ia mac",
  },
  vendor: VENDOR,
  eyebrow: "Comparação",
  h1: "tl;dv vs. Minuta: clipes depois vs. respostas durante",
  intro:
    "O tl;dv é a melhor escolha quando a reunião precisa virar gravação, highlight ou recap para o time compartilhar depois. O Minuta serve o momento ao vivo: o prospect faz uma pergunta em inglês, português ou espanhol, a sala espera, e você precisa saber o que dizer antes da conversa andar.",
  honesty: {
    heading: "Escolha o certo",
    pickThem: {
      name: "tl;dv",
      bullets: [
        "Você quer gravações, clipes polidos e reels de momentos da reunião — esse é o forte do tl;dv.",
        "Você precisa de recaps compartilháveis para quem não estava na sala.",
        "Seu time vive em Salesforce / HubSpot / Notion e as integrações profundas de CRM importam.",
        "Sua prioridade é o artefato depois da call: transcript, resumo, clipe ou handoff para CRM.",
        "Você não se importa com bot na call e com a ata morando na nuvem do tl;dv.",
      ],
    },
    pickMinuta: [
      "Você precisa saber o que dizer durante a conversa, não só depois que ela acaba.",
      "Você precisa de respostas sugeridas a partir do seu contexto e docs enquanto a call ainda acontece.",
      "Bot na call não rola (mercado regulado, prospect paranoico, deal sensível).",
      "Nenhum bot entra na call — o outro lado nunca vê um anotador aparecer. O Minuta roda silencioso no seu desktop.",
      "Você precisa de tradução ao vivo EN ↔ PT/ES durante a call (o tl;dv transcreve várias línguas, mas não traduz ao vivo).",
    ],
  },
  table: {
    heading: "Lado a lado",
    headers: { feature: "Recurso", them: "tl;dv", minuta: "Minuta" },
    rows: [
      {
        feature: "Bot na reunião",
        them: {
          value: "Sim — entra como bot da reunião",
          cite: { url: "https://tldv.io", capturedAt: "2026-05-01" },
        },
        minuta: { value: "Não — invisível, roda no seu computador" },
      },
      {
        feature: "Copiloto sugerindo resposta em tempo real",
        them: { value: "Não — foco em gravações, clipes e recaps depois" },
        minuta: { value: "Sim (Pro) — sugere resposta no momento da pergunta" },
      },
      {
        feature: "Tradução ao vivo (EN ↔ PT/ES)",
        them: { value: "Não — transcrição multilíngue, mas sem tradução em tempo real" },
        minuta: { value: "Sim — tempo real" },
      },
      {
        feature: "Processamento do áudio",
        them: {
          value: "Nuvem",
          cite: { url: "https://tldv.io", capturedAt: "2026-05-01" },
        },
        minuta: { value: "Captura local, API temporária segura" },
      },
      {
        feature: "Onde a ata fica",
        them: { value: "Nuvem do tl;dv" },
        minuta: { value: "No seu computador (processamento temporário)" },
      },
      {
        feature: "Site nativo em PT",
        them: { value: "Não — só em inglês" },
        minuta: { value: "Sim" },
      },
      {
        feature: "Site nativo em ES",
        them: { value: "Não — só em inglês" },
        minuta: { value: "Sim" },
      },
      {
        feature: "Clipes e reels de vídeo",
        them: {
          value: "Sim — esse é o forte deles",
          cite: { url: "https://tldv.io", capturedAt: "2026-05-01" },
        },
        minuta: { value: "Não — foco em copiloto ao vivo, não em editor de clipe" },
      },
      {
        feature: "Integrações profundas com CRM",
        them: {
          value: "Salesforce / HubSpot / Notion / Slack nativos",
          cite: { url: "https://tldv.io", capturedAt: "2026-05-01" },
        },
        minuta: { value: "Export manual (copy/paste, markdown)" },
      },
      {
        feature: "Plano grátis",
        them: {
          value: "~10 transcripts/mês",
          cite: { url: "https://tldv.io/pricing", capturedAt: "2026-05-01" },
        },
        minuta: { value: "7 dias com tudo · depois 1h/mês no Free" },
      },
      {
        feature: "Pago",
        them: {
          value: "Pro ~US$ 29/mês (anual)",
          cite: { url: "https://tldv.io/pricing", capturedAt: "2026-05-01" },
        },
        minuta: { value: "€9/mês" },
      },
    ],
    footnote: "Preços referência 2026-05-01; confirme com cada empresa pra valores atuais.",
  },
  whenMinuta: {
    heading: "Onde o Minuta ganha",
    bullets: [
      "Respostas durante a call. O tl;dv é mais forte quando você precisa de gravações, clipes e recaps depois. O Minuta sugere o que dizer enquanto a pergunta ainda está no ar.",
      "Sugestão com contexto. Minuta pode usar seu contexto e docs para moldar a resposta, em vez de te deixar com um recap quando o momento já passou.",
      "Sem bot na call. Tem prospect que se recusa a falar com bot gravando na sala. O Minuta roda invisível na sua máquina — só você sabe que está lá.",
      "Sem bot na call. O tl;dv entra como participante da reunião. O Minuta escuta do seu desktop — o outro lado nunca vê um anotador entrar. Transcrições, resumos e respostas do copiloto ficam no seu app de desktop.",
      "Tradução ao vivo, não só transcrição multilíngue. O tl;dv transcreve ~30 línguas. O Minuta traduz EN ↔ PT/ES ao vivo — você fala na sua e lê na do outro em tempo real.",
    ],
  },
  also: { heading: "Comparando outras ferramentas?", links: [] },
  cta: {
    heading: "Faça mais do que resumir a call.",
    body: "7 dias com tudo desbloqueado. Traga seu contexto, receba sugestões durante a call e mantenha o bot fora.",
    primary: "Começar trial grátis",
    secondary: "Baixar pro macOS",
  },
};

const es: ComparePageCopy = {
  seo: {
    title: "tl;dv vs. Minuta — clips después vs. respuestas durante",
    description:
      "tl;dv está hecho para grabaciones, clips y recaps compartibles después de la call. Minuta está hecho para conversaciones en inglés, portugués y español donde necesitas saber qué decir ahora.",
    keywords:
      "alternativa tldv, tldv vs minuta, copiloto de reunión en tiempo real, transcripción de reunión mac, tldv español, acta de reunión con ia mac",
  },
  vendor: VENDOR,
  eyebrow: "Comparación",
  h1: "tl;dv vs. Minuta: clips después vs. respuestas durante",
  intro:
    "tl;dv es la mejor opción cuando la reunión tiene que convertirse en grabación, highlight o recap para que el equipo lo comparta después. Minuta sirve el momento en vivo: el prospect hace una pregunta en inglés, portugués o español, la sala espera, y necesitas saber qué decir antes de que la conversación avance.",
  honesty: {
    heading: "Elige el correcto",
    pickThem: {
      name: "tl;dv",
      bullets: [
        "Quieres grabaciones, clips pulidos y reels de momentos de la reunión — ese es el fuerte de tl;dv.",
        "Necesitas recaps compartibles para quienes no estuvieron en la sala.",
        "Tu equipo vive en Salesforce / HubSpot / Notion y las integraciones profundas de CRM importan.",
        "Tu prioridad es el artefacto después de la call: transcript, resumen, clip o handoff a CRM.",
        "No te molesta un bot en la call ni que el acta viva en la nube de tl;dv.",
      ],
    },
    pickMinuta: [
      "Necesitas saber qué decir durante la conversación, no solo después de que termina.",
      "Necesitas respuestas sugeridas desde tu contexto y docs mientras la call sigue pasando.",
      "Bot en la call no funciona (industria regulada, prospect paranoico, deal sensible).",
      "Ningún bot entra a la call — el otro lado nunca ve un anotador unirse. Minuta corre en silencio en tu escritorio.",
      "Necesitas traducción en vivo EN ↔ PT/ES durante la call (tl;dv transcribe muchos idiomas pero no traduce en vivo).",
    ],
  },
  table: {
    heading: "Lado a lado",
    headers: { feature: "Función", them: "tl;dv", minuta: "Minuta" },
    rows: [
      {
        feature: "Bot en la reunión",
        them: {
          value: "Sí — entra como bot de la reunión",
          cite: { url: "https://tldv.io", capturedAt: "2026-05-01" },
        },
        minuta: { value: "No — invisible, corre en tu computadora" },
      },
      {
        feature: "Copiloto sugiriendo respuesta en tiempo real",
        them: { value: "No — foco en grabaciones, clips y recaps después" },
        minuta: { value: "Sí (Pro) — sugiere respuesta en el momento" },
      },
      {
        feature: "Traducción en vivo (EN ↔ PT/ES)",
        them: { value: "No — transcripción multilingüe, pero sin traducción en vivo" },
        minuta: { value: "Sí — tiempo real" },
      },
      {
        feature: "Procesamiento del audio",
        them: {
          value: "Nube",
          cite: { url: "https://tldv.io", capturedAt: "2026-05-01" },
        },
        minuta: { value: "Captura local, API transitoria segura" },
      },
      {
        feature: "Dónde vive el acta",
        them: { value: "Nube de tl;dv" },
        minuta: { value: "En tu computadora (procesamiento transitorio)" },
      },
      {
        feature: "Web nativa en PT",
        them: { value: "No — solo en inglés" },
        minuta: { value: "Sí" },
      },
      {
        feature: "Web nativa en ES",
        them: { value: "No — solo en inglés" },
        minuta: { value: "Sí" },
      },
      {
        feature: "Clips y reels de video",
        them: {
          value: "Sí — ese es su fuerte",
          cite: { url: "https://tldv.io", capturedAt: "2026-05-01" },
        },
        minuta: { value: "No — foco en copiloto en vivo, no en editor de clip" },
      },
      {
        feature: "Integraciones profundas con CRM",
        them: {
          value: "Salesforce / HubSpot / Notion / Slack nativos",
          cite: { url: "https://tldv.io", capturedAt: "2026-05-01" },
        },
        minuta: { value: "Export manual (copy/paste, markdown)" },
      },
      {
        feature: "Plan gratis",
        them: {
          value: "~10 transcripts/mes",
          cite: { url: "https://tldv.io/pricing", capturedAt: "2026-05-01" },
        },
        minuta: { value: "7 días con todo · luego 1h/mes en Gratis" },
      },
      {
        feature: "De pago",
        them: {
          value: "Pro ~US$ 29/mes (anual)",
          cite: { url: "https://tldv.io/pricing", capturedAt: "2026-05-01" },
        },
        minuta: { value: "€9/mes" },
      },
    ],
    footnote: "Precios referencia 2026-05-01; confirma con cada empresa los valores actuales.",
  },
  whenMinuta: {
    heading: "Dónde gana Minuta",
    bullets: [
      "Respuestas durante la call. tl;dv es más fuerte cuando necesitas grabaciones, clips y recaps después. Minuta sugiere qué decir mientras la pregunta sigue en el aire.",
      "Sugerencia con contexto. Minuta puede usar tu contexto y docs para dar forma a la respuesta, en vez de dejarte con un recap cuando el momento ya pasó.",
      "Sin bot en la call. Hay prospects que no aceptan hablar con un bot grabando en la sala. Minuta corre invisible en tu máquina — solo tú sabes que está ahí.",
      "Sin bot en la call. tl;dv entra como participante de la reunión. Minuta escucha desde tu escritorio — el otro lado nunca ve unirse a un anotador. Transcripciones, resúmenes y respuestas del copiloto se guardan en tu app de escritorio.",
      "Traducción en vivo, no solo transcripción multilingüe. tl;dv transcribe ~30 idiomas. Minuta traduce EN ↔ PT/ES en vivo — hablas en el tuyo y lees el del otro en tiempo real.",
    ],
  },
  also: { heading: "¿Comparando otras herramientas?", links: [] },
  cta: {
    heading: "Haz más que resumir la call.",
    body: "7 días con todo desbloqueado. Trae tu contexto, recibe sugerencias durante la call y mantén el bot fuera.",
    primary: "Empezar prueba gratis",
    secondary: "Descargar para macOS",
  },
};

export const compareTldvCopy: ComparePageCopyByLang = { en, pt, es };
